import {
  ICustomProductOptionConfig,
  IShow,
  IShowLocalMap,
  ISupplierCompany,
} from "@evennt-tech/api-v2-core";
import {
  CardInvoiceProps,
  PDFGenerator,
} from "@evennt-tech/api-v2-pdf-templates";
import {uniqBy} from "lodash";
import format from "tools/formats/formatDate";
import {getShowDateFormat} from "constraints/general";
import getTaxAmount from "tools/common/getTaxAmount";
import formatExtraProductName from "tools/formats/formatExtraProductName";
import Fee from "ts/api/fee";
import {ExpandedOrder, OrderProduct} from "ts/api/order";
import {OrderStatusMap} from "tools/order/getOrderStatus";
import {getCpoValue} from "tools/pdf/getCpoValue";
import {getDiscountAmount} from "view/apps/Exhibitor/pages/Cart/helpers";
import {getTotalOrderAmount} from "tools/order/getTotalOrderAmount";
import {SupplierCompanyUtils} from "tools/supplier-company";
import {calculateSubtotal} from "./calculateSubtotal";

export type VatPdfParams = {
  show: IShow;
  order: ExpandedOrder;
  localMap: Pick<IShowLocalMap, "stand">;
  supplierCompany: ISupplierCompany;
  venueName: string;
  venueAddress: string;
  orderStatusMap: OrderStatusMap;
  isSupplierView?: boolean;
  invoiceNumber?: string;
  configById?: Map<string, ICustomProductOptionConfig>;
  supplierCompanyResolvedLogo?: string;
};

const buildProducts = (
  products: OrderProduct[],
  configById?: Map<string, ICustomProductOptionConfig>
): CardInvoiceProps["products"] =>
  products.flatMap((product) => {
    const cpoValue = getCpoValue(
      configById,
      product.cpoConfig?.isCustom ? product.cpoConfig : undefined
    );

    const rows: CardInvoiceProps["products"] = [
      {
        name: [product.name, cpoValue].filter(Boolean).join(" "),
        price: product.price,
        priceWithFees: product.priceWithFees,
        quantity: product.quantity,
      },
    ];

    (product.extras ?? []).forEach((extra) => {
      rows.push({
        name: formatExtraProductName(extra.name),
        price: extra.price,
        priceWithFees: extra.priceWithFees,
        quantity: extra.quantity,
      });
    });

    return rows;
  });

export const buildVatPdfProps = ({
  show,
  order,
  localMap,
  supplierCompany,
  venueName,
  venueAddress,
  orderStatusMap,
  isSupplierView = false,
  invoiceNumber,
  configById,
  supplierCompanyResolvedLogo,
}: VatPdfParams): CardInvoiceProps => {
  const {salesTaxPercentage, salesTaxName, currency} = show;
  const products = order.products.filter(
    (product) => product.owner.id === supplierCompany.id
  );
  const fees = uniqBy(
    products.map((product) => product?.fees).flat(1),
    "id"
  ).filter(Boolean) as Fee[];
  const subtotal = calculateSubtotal(products, fees, true);
  const subtotalExFees = calculateSubtotal(products, fees, false);
  const totalBase = isSupplierView ? subtotalExFees : subtotal;
  const totalAmount = getTotalOrderAmount(
    totalBase + getTaxAmount(totalBase, salesTaxPercentage),
    currency
  );
  const discountAmount = getDiscountAmount(order.products, !isSupplierView);

  return {
    isSupplierView,
    invoiceNumber,
    orderNumber: order.id,
    orderDate: format(order.createdAt.toDate(), getShowDateFormat(show)),
    orderStatus: orderStatusMap,
    currency,
    salesTax: {name: salesTaxName, percentage: salesTaxPercentage},
    showName: show.name,
    supplier: {
      name: supplierCompany.name,
      address: SupplierCompanyUtils.getAddress(supplierCompany, {
        postCode: false,
      }),
      postCode: supplierCompany.postCode,
      VAT: supplierCompany.VAT,
      logo: supplierCompanyResolvedLogo || undefined,
    },
    billing: {
      name: order.billingDetails.name,
      address: order.billingDetails.address,
      postCode: order.billingDetails.postCode,
      vat: order.billingDetails.vat,
    },
    delivery: {
      standLabel: localMap.stand ?? "",
      standName: order.exhibitorConnection?.stand?.name,
      companyName:
        order.exhibitorConnection?.exhibitorPair?.companyToShow?.company
          ?.name || order.billingDetails.tradingName,
      venueName,
      venueAddress,
    },
    products: buildProducts(products, configById),
    fees: fees.map((fee) => ({
      name: fee.name,
      amount: fee.amount,
      amountWithFees: fee.amountWithFees || 0,
    })),
    discountAmount: discountAmount || undefined,
    subtotalAmount: subtotal,
    subtotalExFeesAmount: subtotalExFees,
    totalAmount,
  };
};

export const generateVatPdfBlob = async (params: VatPdfParams): Promise<Blob> =>
  PDFGenerator.cardInvoicePdfBlob(buildVatPdfProps(params));
